import React, { useState } from "react";
import NewChatWindow from "./NewChatWindow";
import { useChatWorkspace } from "../../../context/ChatWorkspaceContext";

interface ChatWithSidebarProps {
  agentId: string;
  agentName: string;
  agentAvatar?: string;
  customerId: string;
  idToken: string;
  model?: string;
  vectorStoreId?: string;
  conversationStarters?: Array<{ id: string; suggestion: string }>;
  agentType?: string;
  tools?: string[];
  onClose?: () => void;
  /** When set with onThreadIdChange, thread selection is controlled (e.g. URL on home). */
  threadId?: string | null;
  onThreadIdChange?: (threadId: string | null) => void;
  availableAgents?: Array<{ id: string; name: string; avatar?: string; agentType?: string }>;
  onAgentChange?: (agentId: string) => void;
}

/**
 * Full-width chat pane; conversation history lives in the app sidebar (Perplexity-style).
 */
export const ChatWithSidebar: React.FC<ChatWithSidebarProps> = ({
  agentId,
  agentName,
  agentAvatar,
  customerId,
  idToken,
  model,
  vectorStoreId,
  conversationStarters,
  agentType,
  tools,
  onClose,
  threadId: threadIdProp,
  onThreadIdChange,
  availableAgents,
  onAgentChange,
}) => {
  const { bumpThreadListRefresh } = useChatWorkspace();
  const [internalThreadId, setInternalThreadId] = useState<string | null>(
    null
  );

  const controlled = onThreadIdChange !== undefined;
  const selectedThreadId = controlled
    ? threadIdProp ?? null
    : internalThreadId;

  const setSelectedThreadId = (tid: string | null) => {
    if (controlled) {
      onThreadIdChange?.(tid);
    } else {
      setInternalThreadId(tid);
    }
  };

  const handleThreadChange = (newThreadId: string | null) => {
    // Only refresh the sidebar when the thread actually changes (e.g. a new chat
    // created its first thread). Continuing the same thread fires onThreadChange
    // on every response — bumping unconditionally reloaded the whole sidebar
    // (loader flash) after each message.
    const threadChanged = newThreadId !== selectedThreadId;
    setSelectedThreadId(newThreadId);
    if (threadChanged) {
      bumpThreadListRefresh();
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 overflow-hidden bg-[#FAFAFA]">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <NewChatWindow
          idToken={idToken}
          agentId={agentId}
          agentName={agentName}
          agentAvatar={agentAvatar}
          customerId={customerId}
          model={model}
          vectorStoreId={vectorStoreId}
          conversationStarters={conversationStarters}
          agentType={agentType}
          tools={tools}
          threadId={selectedThreadId}
          onThreadChange={handleThreadChange}
          onClose={onClose}
          embedded
          availableAgents={availableAgents}
          onAgentChange={onAgentChange}
        />
      </div>
    </div>
  );
};

export default ChatWithSidebar;
